import React, { useState } from 'react'
import './updateMemberModal.scss'
import { Modal, ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import { MembershipDetailed, Member } from 'memberships/schema'
import placeholderPhoto from 'images/member-photo-placeholder.png'
import { UploadInput } from 'uiComponents/input'
import { FormWrapper } from 'uiComponents/form/form'
import TextInputFieldFormik from 'uiComponents/input/textInput/textInputFieldFormik'
import { CancelButton } from 'uiComponents/buttons/cancelButton'
import ActionButton from 'uiComponents/buttons'
import { useUploadApfPhotoMutation } from 'orders/reduxQueries'
import { UploadedFile } from 'orders/ordersService'
import { CameraInput } from 'uiComponents/input/cameraInput'
import DatePickerFieldFormik from 'uiComponents/input/date/datePickerFieldFormik'
import { useTranslation } from 'translations'

interface InputFieldProps {
    name: string
    label: string
}

const InputField = ({ name, label }: InputFieldProps) => (
    <TextInputFieldFormik name={name} className={name} label={label} autoComplete="off" />
)

interface UpdateMemberModalProps {
    membership: MembershipDetailed
    onClose: () => void
    onUpdate: (values: Member, photo: string | null) => void
}

const UpdateMemberModal: React.FC<UpdateMemberModalProps> = ({ membership, onClose, onUpdate }) => {
    const { t } = useTranslation()
    const [uploadApfPhoto] = useUploadApfPhotoMutation()
    const member = membership.member || {}
    const [photo, setPhoto] = useState<string | null>(member?.photo)
    const [showCameraModal, setShowCameraModal] = useState(false)

    const handleUpdateMember = async (values: Member) => {
        await onUpdate(values, photo)
    }

    const useWebcamPhoto = async (file: File) => {
        const uploadedImage = await uploadApfPhoto({ file }).unwrap()
        setPhoto(uploadedImage?.file_id ?? null)
        setShowCameraModal(false)
    }

    return (
        <>
            <FormWrapper initialValues={member} onSubmit={handleUpdateMember}>
                {({ submitForm }) => (
                    <Modal
                        className="membership-details-update-member-modal"
                        title={t('memberships.membershipDetailsPanel.updateMemberModal.title')}
                        onDismiss={onClose}
                    >
                        <ModalBody>
                            <div className="member-photo-container">
                                <a
                                    className="member-current-photo"
                                    href={photo || undefined}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ backgroundImage: `url(${placeholderPhoto})` }}
                                >
                                    {photo && <img src={photo} alt={t('memberships.membershipDetailsPanel.member.photoAlt')} />}
                                </a>
                                <div className="member-new-photo">
                                    <UploadInput
                                        type="img"
                                        fileSizeLimit={16}
                                        onUpload={async (file: File) =>
                                            (await uploadApfPhoto({ file }).unwrap()) ?? {
                                                file_id: '',
                                                file_name: '',
                                            }
                                        }
                                        handleUploadSuccessResponse={(response: UploadedFile) => {
                                            setPhoto(response.file_id)
                                        }}
                                        cropperAspectRatio={7 / 9}
                                        inputId="member-photo"
                                        replaceTopMessages={() => {}}
                                        removeAllMessages={() => {}}
                                        uploadArea
                                        enableCropping
                                    />
                                    <ActionButton
                                        className="take-new-photo-button"
                                        onClick={() => setShowCameraModal(true)}
                                        secondary
                                    >
                                        {t('memberships.membershipDetailsPanel.updateMemberModal.takePhotoWithCamera')}
                                    </ActionButton>
                                </div>
                            </div>
                            <div className="update-member-form">
                                <div className="form-row row-1">
                                    <InputField name="firstName" label={t('memberships.membershipDetailsPanel.member.firstName')} />
                                    <InputField name="lastName" label={t('memberships.membershipDetailsPanel.member.lastName')} />
                                    <DatePickerFieldFormik
                                        name="dateOfBirth"
                                        className="date-of-birth"
                                        label={t('memberships.membershipDetailsPanel.member.dateOfBirth')}
                                        allowNullDate
                                        lowerBoundary={new Date('1900-01-01')}
                                        upperBoundary={new Date()}
                                    />
                                </div>
                                <div className="form-row row-2">
                                    <InputField name="phoneNumber" label={t('memberships.membershipDetailsPanel.member.phoneNumber')} />
                                    <InputField name="streetName" label={t('memberships.membershipDetailsPanel.member.streetAddress')} />
                                    <InputField name="zipCode" label={t('memberships.membershipDetailsPanel.member.postCode')} />
                                </div>
                                <div className="form-row row-3">
                                    <InputField name="city" label={t('memberships.membershipDetailsPanel.member.city')} />
                                    <InputField name="country" label={t('memberships.membershipDetailsPanel.member.country')} />
                                </div>
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <CancelButton onClick={onClose} />
                            <ActionButton className="submit-button" onClick={submitForm}>
                                {t('memberships.membershipDetailsPanel.updateMemberModal.saveChanges')}
                            </ActionButton>
                        </ModalFooter>
                    </Modal>
                )}
            </FormWrapper>
            {showCameraModal && (
                <Modal
                    className="membership-details-camera-modal"
                    title={t('memberships.membershipDetailsPanel.updateMemberModal.takePhotoTitle')}
                    onDismiss={() => setShowCameraModal(false)}
                >
                    <ModalBody>
                        <CameraInput onChange={useWebcamPhoto} />
                    </ModalBody>
                </Modal>
            )}
        </>
    )
}

export default UpdateMemberModal
